import { getUrlWithoutDomain } from '@/helpers/getUrlWithoutDomain';
import type { Comedian_Entry, Image, LanguageCategory, ShowCategory } from '@/types/custom';
import type { Event_Entry } from '@/types/graphql';
import { format } from 'date-fns';

export function formatShowDate(date: string | null | undefined): string {
    if (!date) return '';
    return format(new Date(date), 'EEEE d MMMM yyyy');
}

export function getShowStatus(event: Event_Entry): string {
    if (event.eventTicketLinkOverride) return 'active';
    if (event.eventTicketSaleStart && Date.parse(event.eventTicketSaleStart) > Date.now())
        return 'upcoming';
    return event.eventStatus || '';
}

export function getShowFlag(event: Event_Entry): string {
    const languages =
        event.eventLanguage
            ?.map((lang) => (lang as LanguageCategory).countryCode)
            .filter(Boolean) || [];
    return languages[0] || 'nl';
}

export function getShowPrimaryLanguage(event: Event_Entry): string {
    const languages =
        event.eventLanguage?.map((lang) => (lang as LanguageCategory).language).filter(Boolean) ||
        [];
    return languages[0] || 'nl';
}

export function getShowTitle(event: Event_Entry): string {
    return event.eventTitle?.replace(/\([0-9][0-9]:[0-9][0-9]\)/g, '') || '';
}

export function getShowSubtitle(event: Event_Entry): string {
    return event.eventSubtitle || '';
}

export function getShowDescription(event: Event_Entry): string {
    return event.eventDescription?.replace(/(?:\r\n|\r|\n)/g, '  \n') || '';
}

export function getShowHeaderImage(event: Event_Entry) {
    return event.eventImages?.[0] || null;
}

export function getShowFirstAdditionalImage(event: Event_Entry) {
    return event.additionalEventImages?.[0] || event.eventImages?.[0] || null;
}

export function getShowArtists(event: Event_Entry) {
    return (event.artists || []) as Comedian_Entry[];
}

export function getShowCategories(event: Event_Entry) {
    return (event.eventCategories || []) as ShowCategory[];
}

export function getShowAlternativeLanguages(event: Event_Entry) {
    return event.localized || [];
}

export function getShowTicketPrice(event: Event_Entry): number | null {
    return event.eventTicketPrices?.[0]?.ticketPrice || null;
}

export function getShowTicketName(event: Event_Entry): string | null {
    return event.eventTicketPrices?.[0]?.ticketName || null;
}

export function formatShowPrice(price: number | null): string {
    return price ? `€${price.toFixed(2)}` : '';
}

export function getShowProgramStart(event: Event_Entry): string {
    return event.eventProgramStart || '';
}

export function getShowProgramEnd(event: Event_Entry): string {
    return event.eventProgramEnd || '';
}

export function getShowPrices(event: Event_Entry) {
    return event.eventTicketPrices || [];
}

export function getShowPageLink(event: Event_Entry): string {
    return getUrlWithoutDomain(event.url || '') || '';
}

export function getShowTicketLink(event: Event_Entry): string {
    return event.eventTicketLinkOverride || event.eventTicketLink || '';
}

export function getShowImage(event: Event_Entry) {
    return event.eventImages?.[0] as Image | undefined;
}

export function getAdditionalShowImages(event: Event_Entry) {
    return event.additionalEventImages?.[0] as Image | undefined;
}

export function getShowLanguages(event: Event_Entry) {
    return (event.eventLanguage || []) as LanguageCategory[];
}

export function getShowVenue(event: Event_Entry): string {
    return event.venue?.[0]?.title || '';
}

export function getShowEventStatus(event: Event_Entry): string {
    if (event.eventTicketLinkOverride) {
        return 'active';
    }

    if (event.eventTicketSaleStart && Date.parse(event.eventTicketSaleStart) > Date.now()) {
        return 'upcoming';
    }

    return event.eventStatus || '';
}

export function getShowAlmostSoldOut(event: Event_Entry): boolean {
    return event.eventTotalTicketsRemaining < 0.1 * event.eventTotalTicketsAmount;
}

export function getShowButtonText(event: Event_Entry): string {
    const status = getShowEventStatus(event);

    if (status === 'upcoming') {
        return 'upcoming';
    }

    if (status === 'active' && !event.eventTicketLinkOverride && getShowAlmostSoldOut(event)) {
        return 'latest-tickets';
    }

    if (status === 'sold-out') {
        return 'sold-out';
    }

    if (status === 'cancelled') {
        return 'cancelled';
    }

    return 'buy-tickets';
}

export function getShouldDisplayPurchaseButton(event: Event_Entry): boolean {
    return (
        new Date(event.eventProgramStart || '') > new Date() &&
        (event.eventStatus === 'active' || event.eventStatus === 'upcoming')
    );
}

export function getShowButtonVariant(event: Event_Entry): string {
    const status = getShowEventStatus(event);

    if (status === 'upcoming') {
        return 'upcoming';
    }

    if (status === 'active' && !event.eventTicketLinkOverride && getShowAlmostSoldOut(event)) {
        return 'almost-sold-out';
    }

    if (status === 'sold-out') {
        return 'sold-out';
    }

    if (status === 'cancelled') {
        return 'cancelled';
    }

    return 'active';
}

export function getShowEventColor(event: Event_Entry): string {
    if (event.eventStatus === 'upcoming') {
        return 'upcoming';
    }

    if (
        event.eventStatus === 'active' &&
        !event.eventTicketLinkOverride &&
        getShowAlmostSoldOut(event)
    ) {
        return 'almost-sold-out';
    }

    if (event.eventStatus === 'sold-out') {
        return 'sold-out';
    }

    if (event.eventStatus === 'cancelled') {
        return 'cancelled';
    }

    return 'active';
}
